import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Popover
} from '@mui/material';
import {
  Business as BusinessIcon,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
  ConfirmationNumber as TicketIcon,
  ClearAll as ClearAllIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRecentlyViewed, type RecentEntityType, type RecentItem } from '../../hooks/useRecentlyViewed';

const ICONS: Record<RecentEntityType, React.ReactElement> = {
  client: <BusinessIcon fontSize="small" />,
  project: <FolderIcon fontSize="small" />,
  task: <AssignmentIcon fontSize="small" />,
  ticket: <TicketIcon fontSize="small" />
};

const PATH: Record<RecentEntityType, (id: string) => string> = {
  client: (id) => `/clients/${id}`,
  project: (id) => `/projects/${id}`,
  task: (id) => `/tasks/${id}`,
  ticket: (id) => `/tickets/${id}`
};

const RecentlyViewedList: React.FC = () => {
  const navigate = useNavigate();
  const { items, clear } = useRecentlyViewed();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  if (items.length === 0) return null;

  const handleItemClick = (item: RecentItem) => {
    navigate(PATH[item.type](item.id));
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="Recently viewed">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={open}
          sx={{ color: 'inherit', opacity: 0.85 }}
        >
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260, maxWidth: 360, py: 1 } } }}
      >
        <Box sx={{ px: 2, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="overline" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
            Recently viewed
          </Typography>
          <Tooltip title="Clear">
            <IconButton size="small" onClick={clear} sx={{ opacity: 0.7 }}>
              <ClearAllIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <List dense sx={{ py: 0 }}>
          {items.map((item) => (
            <ListItemButton
              key={`${item.type}:${item.id}`}
              onClick={() => handleItemClick(item)}
              sx={{ py: 0.25 }}
            >
              <ListItemIcon sx={{ minWidth: 32, opacity: 0.85 }}>
                {ICONS[item.type]}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default RecentlyViewedList;
